/**
 * SingleSelect — React (web) implementation.
 *
 * All styling references the shared variant maps in SingleSelect.ts,
 * which in turn reference the colour/typography elements.
 * No raw hex values or pixel font sizes in this file.
 */

/// <reference lib="dom" />

import React, { useRef, useCallback } from 'react';
import type { SingleSelectOption, SingleSelectProps } from './SingleSelect';
import {
  firstEnabledOptionIndex,
  nextEnabledOptionIndex,
  optionCardColorsFor,
  OPTION_BORDER_WIDTH,
  OPTION_CARD_RADIUS,
  OPTION_CARD_PADDING,
  OPTION_TEXT_GAP,
  LABEL_TO_OPTIONS_GAP,
  OPTION_GAP,
  LABEL_TYPOGRAPHY,
  OPTION_LABEL_TYPOGRAPHY,
  OPTION_DESCRIPTION_TYPOGRAPHY,
  DISABLED_OPACITY,
  isRevealMode,
  INLINE_SELECT_DESCRIPTION_TYPOGRAPHY,
  INLINE_SELECT_DESCRIPTION_COLOR,
  INLINE_SELECT_DIVIDER_COLOR,
  INLINE_SELECT_BORDER_COLOR,
  INLINE_SELECT_BORDER_WIDTH,
  INLINE_SELECT_SELECTED_FILL,
  INLINE_SELECT_SELECTED_TEXT,
  INLINE_SELECT_HORIZONTAL_PADDING,
  INLINE_SELECT_DESCRIPTION_GAP,
  INLINE_SELECT_TRACK_COLOR,
  INLINE_SELECT_TRACK_RADIUS,
  INLINE_SELECT_TRACK_PADDING,
  INLINE_SELECT_SELECTED_TYPOGRAPHY,
  INLINE_SELECT_PADDING,
  INLINE_SELECT_ROW_PADDING,
  INLINE_SELECT_OPTION_WIDTH,
  INLINE_SELECT_GAP,
  INLINE_SELECT_WEB_MIN_HEIGHT,
  INLINE_SELECT_LABEL_TYPOGRAPHY,
} from './SingleSelect';
import { OptionFeedbackRow } from './OptionFeedback.web';
import { revealedBorderStyle } from './optionFeedback';
import { OptionTag, webOptionAccessibilityLabel } from './optionTag.web';
import { singleSelectPressHandler, type SingleSelectSelection } from './selection';
import {
  RADIO_SIZE,
  RADIO_RADIUS,
  RADIO_BORDER_WIDTH,
  RADIO_TICK_ICON,
  RADIO_TICK_SIZE,
  RADIO_TICK_COLOR,
  radioDotColorsFor,
} from '../Radio/Radio';
import { Icon } from '../Icon/Icon.web';
import { ensureFocusVisibleStyle } from '../../elements/focusStyle.web';
import { mouseHoverHandlers } from '../../elements/mouseHover.web';
import {
  webOptionCardBackground,
  webOptionCardBorder,
  webOptionIndicatorBorder,
  WEB_OPTION_CONTENT_GAP,
} from './optionCardStates.web';
import {
  infoDotOptionButtonStyle,
  OptionCardFrame,
  OptionInfoDotContent,
  optionInfoDotReveal,
} from './optionInfoDot.web';
import { colors } from '../../elements/colors';
import { radius } from '../../elements/radius';
import { sizing } from '../../elements/sizing';
import { spacing } from '../../elements/spacing';

/** A picture drawn at the start of an option card. Web only. */
export interface WebSingleSelectThumbnail {
  src: string;
  /** The fill behind a picture with transparent edges. */
  backgroundColor?: string;
}

/**
 * The thumbnail's drawn size. The v9 web option card draws it 72px square;
 * there is no thumbnail token, so it is composed from the touch-target and
 * large spacing tokens.
 */
const WEB_OPTION_THUMBNAIL_SIZE = sizing.touchTarget.minimum + spacing.lg;
const WEB_OPTION_THUMBNAIL_RADIUS = radius.lg;
/**
 * A card with a thumbnail tightens its leading padding and its gaps, as the v9
 * card does, so the text keeps a readable column beside the picture.
 */
const WEB_OPTION_THUMBNAIL_GAP = spacing.m;
const WEB_OPTION_THUMBNAIL_LEADING_PADDING = spacing.m;

export type WebSingleSelectProps = {
  actionId?: string;
  /** Analytics id for every option's info dot; the generic one when omitted. */
  infoDotActionId?: string;
  label: string;
  labelDescription?: SingleSelectProps['labelDescription'];
  hideLabel?: boolean;
  layout?: SingleSelectProps['layout'];
  errorText?: string;
  value?: string | null;
  options: SingleSelectOption[];
  /** Shows every card in its error look and marks the group invalid. Web only. */
  invalid?: boolean;
  /**
   * Web only. A decorative picture per option value, drawn at the start of
   * that option's card. Options without an entry draw none.
   */
  optionThumbnails?: Readonly<Record<string, WebSingleSelectThumbnail>>;
  'data-testid'?: string;
  'aria-label'?: string;
} & SingleSelectSelection;

export function SingleSelect({
  actionId = 'ui.single-select.change',
  infoDotActionId = 'ui.info-dot.toggle',
  ...props
}: WebSingleSelectProps) {
  const {
    label,
    labelDescription,
    hideLabel = false,
    errorText,
    value,
    options,
    onSelect,
    invalid = false,
    optionThumbnails,
    'data-testid': testId,
    'aria-label': ariaLabel,
  } = props;
  const inline = props.layout === 'inline';
  const handlePress = singleSelectPressHandler(props, value);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // A revealed list shows a committed answer: arrow keys still move focus so
  // every result can be read, but nothing changes the selection.
  const readOnly = isRevealMode(options);

  // Roving tabindex: the selected option holds the tab stop; with nothing
  // selected, a value no option matches, or a selected option that is now
  // disabled, the first selectable option does, so the group stays reachable.
  const selectedIndex = options.findIndex((o) => !o.disabled && o.value === value);
  const focusableIndex = selectedIndex === -1 ? firstEnabledOptionIndex(options) : selectedIndex;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const currentIndex = optionRefs.current.findIndex((ref) => ref === document.activeElement);
      if (currentIndex === -1) return;

      let nextIndex: number | undefined;
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault();
        nextIndex = nextEnabledOptionIndex(options, currentIndex, 1);
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault();
        nextIndex = nextEnabledOptionIndex(options, currentIndex, -1);
      }

      if (nextIndex !== undefined && nextIndex !== currentIndex) {
        optionRefs.current[nextIndex]?.focus();
        if (!readOnly) onSelect?.(options[nextIndex].value);
      }
    },
    [options, onSelect, readOnly],
  );

  const labelStyle: React.CSSProperties = {
    fontFamily: LABEL_TYPOGRAPHY.fontFamily,
    fontWeight: LABEL_TYPOGRAPHY.fontWeight,
    fontSize: LABEL_TYPOGRAPHY.fontSize,
    lineHeight: `${LABEL_TYPOGRAPHY.lineHeight}px`,
    letterSpacing: LABEL_TYPOGRAPHY.letterSpacing,
    color: errorText ? colors.text.alert : optionCardColorsFor(false).labelText,
    ...(inline
      ? {
          flex: 1,
          fontFamily: INLINE_SELECT_LABEL_TYPOGRAPHY.fontFamily,
          fontWeight: INLINE_SELECT_LABEL_TYPOGRAPHY.fontWeight,
          fontSize: INLINE_SELECT_LABEL_TYPOGRAPHY.fontSize,
          lineHeight: `${INLINE_SELECT_LABEL_TYPOGRAPHY.lineHeight}px`,
        }
      : {}),
  };

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: inline ? 'row' : 'column',
    alignItems: inline ? 'center' : undefined,
    paddingBlock: inline ? INLINE_SELECT_ROW_PADDING : undefined,
    paddingLeft: inline ? INLINE_SELECT_PADDING : undefined,
    borderBottom: inline
      ? `${INLINE_SELECT_BORDER_WIDTH}px solid ${INLINE_SELECT_DIVIDER_COLOR}`
      : undefined,
    gap: inline ? INLINE_SELECT_GAP : LABEL_TO_OPTIONS_GAP,
  };

  const optionsStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: inline ? 'row' : 'column',
    gap: inline ? INLINE_SELECT_GAP : OPTION_GAP,
    padding: inline ? INLINE_SELECT_TRACK_PADDING : undefined,
    backgroundColor: inline ? INLINE_SELECT_TRACK_COLOR : undefined,
    borderRadius: inline ? INLINE_SELECT_TRACK_RADIUS : undefined,
  };

  return (
    <div style={containerStyle} data-testid={testId}>
      {(!hideLabel || errorText) && (
        <div style={{ flex: inline ? 1 : undefined }}>
          <div
            style={labelStyle}
            role={errorText ? 'status' : undefined}
            aria-live="polite"
            data-testid={testId ? `${testId}-label` : undefined}
          >
            {errorText ?? label}
          </div>
          {labelDescription && (
            <div
              style={{
                ...INLINE_SELECT_DESCRIPTION_TYPOGRAPHY,
                lineHeight: `${INLINE_SELECT_DESCRIPTION_TYPOGRAPHY.lineHeight}px`,
                color: INLINE_SELECT_DESCRIPTION_COLOR,
                marginTop: INLINE_SELECT_DESCRIPTION_GAP,
              }}
            >
              {labelDescription}
            </div>
          )}
        </div>
      )}
      <div
        role="radiogroup"
        aria-label={[ariaLabel ?? label, labelDescription].filter(Boolean).join(', ')}
        aria-readonly={readOnly || undefined}
        aria-invalid={invalid || !!errorText || undefined}
        style={optionsStyle}
        onKeyDown={handleKeyDown}
      >
        {options.map((option, index) => {
          const selected = value === option.value;
          return (
            /* analytics-exempt: forwarded-wrapper */
            <OptionCard
              actionId={actionId}
              infoDotActionId={infoDotActionId}
              key={option.value}
              ref={(el) => {
                optionRefs.current[index] = el;
              }}
              compact={inline}
              option={option}
              thumbnail={optionThumbnails?.[option.value]}
              selected={selected}
              disabled={option.disabled === true}
              readOnly={readOnly}
              invalid={invalid}
              tabIndex={index === focusableIndex && !option.disabled ? 0 : -1}
              onPress={() => handlePress?.(option.value)}
              data-testid={testId ? `${testId}-option-${option.value}` : undefined}
            />
          );
        })}
      </div>
    </div>
  );
}

interface OptionCardProps {
  compact: boolean;
  actionId: string;
  infoDotActionId: string;
  option: SingleSelectOption;
  thumbnail?: WebSingleSelectThumbnail;
  selected: boolean;
  disabled: boolean;
  readOnly: boolean;
  invalid: boolean;
  tabIndex: 0 | -1;
  onPress: () => void;
  'data-testid'?: string;
}

const FOCUS_STYLE_ID = 'ds-singleselect-focus';

const OptionCard = React.forwardRef<HTMLButtonElement, OptionCardProps>(
  (
    {
      compact,
      actionId,
      infoDotActionId,
      option,
      thumbnail,
      selected,
      disabled,
      readOnly,
      invalid,
      tabIndex,
      onPress,
      'data-testid': testId,
    },
    ref,
  ) => {
    const [pointerOver, setPointerOver] = React.useState(false);
    const [infoExpanded, setInfoExpanded] = React.useState(false);
    const descriptionId = React.useId();
    // A revealed (answer-feedback) option is read-only, so it takes no hover affordance.
    const hovered = pointerOver && !disabled && !readOnly;
    const reveal = optionInfoDotReveal(option);
    React.useEffect(() => {
      ensureFocusVisibleStyle(FOCUS_STYLE_ID, ['[data-ds-singleselect-option]']);
    }, []);
    const cardColors = optionCardColorsFor(selected);
    const revealed = revealedBorderStyle(option);

    const webState = { selected, hovered, invalid };

    const cardStyle: React.CSSProperties = {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: compact
        ? INLINE_SELECT_GAP
        : thumbnail
          ? WEB_OPTION_THUMBNAIL_GAP
          : WEB_OPTION_CONTENT_GAP,
      padding: compact
        ? `${INLINE_SELECT_PADDING}px ${INLINE_SELECT_HORIZONTAL_PADDING}px`
        : OPTION_CARD_PADDING,
      ...(!compact && thumbnail ? { paddingLeft: WEB_OPTION_THUMBNAIL_LEADING_PADDING } : {}),
      minHeight: compact ? INLINE_SELECT_WEB_MIN_HEIGHT : undefined,
      borderRadius: compact ? INLINE_SELECT_TRACK_RADIUS : OPTION_CARD_RADIUS,
      backgroundColor:
        compact && selected
          ? INLINE_SELECT_SELECTED_FILL
          : compact
            ? 'transparent'
            : webOptionCardBackground(cardColors.background, webState),
      border: compact
        ? `${INLINE_SELECT_BORDER_WIDTH}px solid ${selected ? INLINE_SELECT_SELECTED_FILL : INLINE_SELECT_BORDER_COLOR}`
        : `${OPTION_BORDER_WIDTH}px ${revealed?.borderStyle ?? 'solid'} ${revealed?.borderColor ?? webOptionCardBorder(cardColors.border, webState)}`,
      cursor: disabled || readOnly ? 'default' : 'pointer',
      textAlign: 'left',
      fontFamily: OPTION_LABEL_TYPOGRAPHY.fontFamily,
      width: compact ? undefined : '100%',
      minWidth: compact ? INLINE_SELECT_OPTION_WIDTH : undefined,
      boxSizing: 'border-box',
      opacity: disabled ? DISABLED_OPACITY : 1,
    };

    const textContentStyle: React.CSSProperties = {
      display: 'flex',
      flexDirection: 'column',
      gap: OPTION_TEXT_GAP,
      flex: 1,
    };

    const optionLabelStyle: React.CSSProperties = {
      fontFamily: OPTION_LABEL_TYPOGRAPHY.fontFamily,
      fontWeight: compact
        ? INLINE_SELECT_SELECTED_TYPOGRAPHY.fontWeight
        : OPTION_LABEL_TYPOGRAPHY.fontWeight,
      fontSize: compact
        ? INLINE_SELECT_SELECTED_TYPOGRAPHY.fontSize
        : OPTION_LABEL_TYPOGRAPHY.fontSize,
      lineHeight: `${compact ? INLINE_SELECT_SELECTED_TYPOGRAPHY.lineHeight : OPTION_LABEL_TYPOGRAPHY.lineHeight}px`,
      letterSpacing: OPTION_LABEL_TYPOGRAPHY.letterSpacing,
      color: compact && selected ? INLINE_SELECT_SELECTED_TEXT : cardColors.labelText,
    };

    const descriptionStyle: React.CSSProperties = {
      fontFamily: OPTION_DESCRIPTION_TYPOGRAPHY.fontFamily,
      fontWeight: OPTION_DESCRIPTION_TYPOGRAPHY.fontWeight,
      fontSize: OPTION_DESCRIPTION_TYPOGRAPHY.fontSize,
      lineHeight: `${OPTION_DESCRIPTION_TYPOGRAPHY.lineHeight}px`,
      letterSpacing: OPTION_DESCRIPTION_TYPOGRAPHY.letterSpacing,
      color: cardColors.descriptionText,
    };

    const indicatorColors = radioDotColorsFor(selected);
    const indicatorStyle: React.CSSProperties = {
      width: compact ? RADIO_TICK_SIZE : RADIO_SIZE,
      height: compact ? RADIO_TICK_SIZE : RADIO_SIZE,
      // The border sits inside the size, so the drawn circle is the size.
      boxSizing: 'border-box',
      borderRadius: RADIO_RADIUS,
      border: compact
        ? undefined
        : `${RADIO_BORDER_WIDTH}px solid ${webOptionIndicatorBorder(indicatorColors.border, webState)}`,
      backgroundColor: compact ? 'transparent' : indicatorColors.background,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    };

    const indicator = compact ? null : (
      <span style={indicatorStyle} aria-hidden="true">
        {selected ? (
          <Icon
            name={RADIO_TICK_ICON}
            size={RADIO_TICK_SIZE}
            color={compact ? cardColors.labelText : RADIO_TICK_COLOR}
          />
        ) : null}
      </span>
    );

    const content = reveal ? (
      <OptionInfoDotContent
        infoDotActionId={infoDotActionId}
        label={option.label}
        tag={option.tag}
        reveal={reveal}
        expanded={infoExpanded}
        descriptionId={descriptionId}
        onToggle={() => setInfoExpanded((open) => !open)}
        textContentStyle={textContentStyle}
        labelStyle={optionLabelStyle}
        descriptionStyle={descriptionStyle}
        data-testid={testId}
      />
    ) : null;

    return (
      <OptionCardFrame
        reveal={reveal}
        cardStyle={cardStyle}
        content={content}
        indicator={indicator}
      >
        <button
          data-analytics-action={actionId}
          ref={ref}
          type="button"
          role="radio"
          aria-checked={selected}
          aria-disabled={disabled || undefined}
          aria-label={webOptionAccessibilityLabel(option)}
          aria-describedby={reveal && infoExpanded ? descriptionId : undefined}
          tabIndex={tabIndex}
          onClick={disabled || readOnly ? undefined : onPress}
          {...mouseHoverHandlers(setPointerOver)}
          style={reveal ? infoDotOptionButtonStyle(disabled) : cardStyle}
          data-testid={testId}
          data-ds-singleselect-option
        >
          {reveal ? null : (
            <>
              {thumbnail ? (
                <img
                  src={thumbnail.src}
                  alt=""
                  width={WEB_OPTION_THUMBNAIL_SIZE}
                  height={WEB_OPTION_THUMBNAIL_SIZE}
                  style={{
                    flexShrink: 0,
                    width: WEB_OPTION_THUMBNAIL_SIZE,
                    height: WEB_OPTION_THUMBNAIL_SIZE,
                    objectFit: 'cover',
                    borderRadius: WEB_OPTION_THUMBNAIL_RADIUS,
                    backgroundColor: thumbnail.backgroundColor,
                  }}
                />
              ) : null}
              <div style={textContentStyle}>
                {option.tag ? (
                  <OptionTag tag={option.tag} data-testid={testId ? `${testId}-tag` : undefined} />
                ) : null}
                <span style={optionLabelStyle}>{option.label}</span>
                {option.description && <span style={descriptionStyle}>{option.description}</span>}
                <OptionFeedbackRow
                  option={option}
                  data-testid={testId ? `${testId}-feedback` : undefined}
                />
              </div>
              {indicator}
            </>
          )}
        </button>
      </OptionCardFrame>
    );
  },
);

OptionCard.displayName = 'OptionCard';
